import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { ApiResponseRegistryNameservers } from '../types/api/ApiResponseDomainName';

interface UseRefreshRegistryNameserversReturn {
  refreshing: boolean;
  error: string | null;
  /** Resolves true on success (registration_nameservers persisted server-side), false on failure. */
  refresh: () => Promise<boolean>;
}

/**
 * Action hook for the registry-refresh write (POST …/nameservers/registry/refresh) — resets the
 * stored `registration_nameservers` from the registrar. Unlike `useEffectiveNameservers` this does
 * NOT auto-fetch on mount and does NOT hold the nameserver data: on success the parent reloads the
 * whole domain (via `onConfigured`), keeping `registration_nameservers` single-sourced from the
 * loaded `domainName`. Mirrors `useEffectiveNameservers`/`ConfigureNameserversModal` for the
 * `useApiFetch` + JSend conventions — note that backend failures are JSend `error` at HTTP 200
 * (so `response.ok` stays `true`), hence success is decided by `data.status === 'success'`.
 */
export const useRefreshRegistryNameservers = (id: number): UseRefreshRegistryNameserversReturn => {
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiFetch = useApiFetch();

  const refresh = useCallback(async (): Promise<boolean> => {
    if (id <= 0) {
      return false;
    }
    try {
      setRefreshing(true);
      setError(null);

      // POST, no body — the new value comes entirely from the registrar.
      const response = await apiFetch(`/datacenters/domain-names/${id}/nameservers/registry/refresh`, {
        method: 'POST',
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseRegistryNameservers;
      if (data.status === 'success') {
        return true;
      }
      // Failures arrive as JSend error at HTTP 200; the Support-directed reason is in `message`.
      setError(data.message ?? 'Failed to refresh registry nameservers');
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while refreshing registry nameservers');
      return false;
    } finally {
      setRefreshing(false);
    }
  }, [apiFetch, id]);

  return { refreshing, error, refresh };
};
